/**
 * API Client for Analyst Notes
 *
 * Handles table and column notes (analyst annotations)
 */

import { httpClient } from './client';
import {
  assertObject,
  assertEntityId,
  assertString,
  assertNumber,
  assertEntityIdOrNullish,
  assertArrayOf,
} from '../types/validation';

// ============================================================================
// Runtime Validators
// ============================================================================

/**
 * Перечень идентификаторов тегов: они числовые (dto-conventions-R04), и клиент
 * принимает как число, так и его строковую запись.
 */
function assertEntityIdArray(value: unknown, path: string): readonly string[] {
  return assertArrayOf(value, assertEntityId, path);
}

/**
 * Отсутствие заметки — нормальное состояние, а не отказ (notes-R08):
 * чтение отвечает успехом с пустым результатом. Пустым считается тело `null`,
 * отсутствующее тело и пустая строка (ответ без содержимого).
 */
function isEmptyNoteResult(data: unknown): boolean {
  return data === null || data === undefined || data === '';
}

export function validateTableNote(data: unknown, path: string): ITableNote {
  const obj = assertObject(data, path);
  return {
    tableId: assertEntityId(obj['tableId'], `${path}.tableId`),
    note: assertString(obj['note'], `${path}.note`),
    tagIds: assertEntityIdArray(obj['tagIds'], `${path}.tagIds`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    updatedBy: assertString(obj['updatedBy'], `${path}.updatedBy`),
  };
}

export function validateColumnNote(data: unknown, path: string): IColumnNote {
  const obj = assertObject(data, path);
  return {
    columnId: assertEntityId(obj['columnId'], `${path}.columnId`),
    note: assertString(obj['note'], `${path}.note`),
    statusId: assertEntityIdOrNullish(obj['statusId'], `${path}.statusId`),
    tagIds: assertEntityIdArray(obj['tagIds'], `${path}.tagIds`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    updatedBy: assertString(obj['updatedBy'], `${path}.updatedBy`),
  };
}

export function validateTableNotes(data: unknown, path: string): ITableNotes {
  const obj = assertObject(data, path);
  return {
    tableNote: obj['tableNote'] === null || obj['tableNote'] === undefined
      ? null
      : validateTableNote(obj['tableNote'], `${path}.tableNote`),
    columnNotes: assertArrayOf(obj['columnNotes'], validateColumnNote, `${path}.columnNotes`),
  };
}

export function validateSchemaNote(data: unknown, path: string): ISchemaNote {
  const obj = assertObject(data, path);
  return {
    schemaId: assertNumber(obj['schemaId'], `${path}.schemaId`),
    note: assertString(obj['note'], `${path}.note`),
    tagIds: assertEntityIdArray(obj['tagIds'], `${path}.tagIds`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    updatedBy: assertString(obj['updatedBy'], `${path}.updatedBy`),
  };
}

export function validateSourceNote(data: unknown, path: string): ISourceNote {
  const obj = assertObject(data, path);
  return {
    sourceId: assertEntityId(obj['sourceId'], `${path}.sourceId`),
    note: assertString(obj['note'], `${path}.note`),
    tagIds: assertEntityIdArray(obj['tagIds'], `${path}.tagIds`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    updatedBy: assertString(obj['updatedBy'], `${path}.updatedBy`),
  };
}

export function validateGroupNote(data: unknown, path: string): IGroupNote {
  const obj = assertObject(data, path);
  return {
    groupId: assertEntityId(obj['groupId'], `${path}.groupId`),
    note: assertString(obj['note'], `${path}.note`),
    tagIds: assertEntityIdArray(obj['tagIds'], `${path}.tagIds`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
    updatedBy: assertString(obj['updatedBy'], `${path}.updatedBy`),
  };
}


// ============================================================================
// Type Definitions
// ============================================================================

export interface ITableNote {
  readonly tableId: string;
  readonly note: string;
  readonly tagIds: readonly string[];
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface IColumnNote {
  readonly columnId: string;
  readonly note: string;
  readonly statusId?: string | null; // UUID
  readonly tagIds: readonly string[]; // Array of UUIDs
  readonly updatedAt: string;
  readonly updatedBy: string;
}

/**
 * Тело сохранения заметки: текст и полный список тегов.
 *
 * Объект адресуется каноническим идентификатором в адресе запроса — тело его
 * не дублирует. Автора и момент изменения проставляет сервер.
 */
export interface INoteSaveRequest {
  readonly note: string;
  readonly tagIds: readonly string[];
}

/** Тело сохранения заметки колонки: дополнительно несёт статус. */
export interface IColumnNoteSaveRequest extends INoteSaveRequest {
  readonly statusId?: string | null;
}

export interface ITableNotes {
  readonly tableNote: ITableNote | null;
  readonly columnNotes: readonly IColumnNote[];
}

export interface ISchemaNote {
  readonly schemaId: number;
  readonly note: string;
  readonly tagIds: readonly string[];
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface ISourceNote {
  readonly sourceId: string;
  readonly note: string;
  readonly tagIds: readonly string[];
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface IGroupNote {
  readonly groupId: string;
  readonly note: string;
  readonly tagIds: readonly string[];
  readonly updatedAt: string;
  readonly updatedBy: string;
}

// Batch operations
export interface IBatchAppendNoteRequest {
  readonly action: "appendNote";
  readonly columnIds: readonly string[];
  readonly noteToAppend: string;
}

export interface INotesApiClient {
  getTableNotes(projectId: string, tableId: string): Promise<ITableNotes>;
  saveTableNote(
    projectId: string,
    tableId: string,
    note: INoteSaveRequest
  ): Promise<ITableNote>;
  saveColumnNote(
    projectId: string,
    columnId: string,
    note: IColumnNoteSaveRequest
  ): Promise<IColumnNote>;
  batchAppendNote(
    projectId: string,
    columnIds: readonly string[],
    noteToAppend: string
  ): Promise<void>;
  getSchemaNote(
    projectId: string,
    sourceId: string,
    schemaId: number
  ): Promise<ISchemaNote | null>;
  saveSchemaNote(
    projectId: string,
    sourceId: string,
    schemaId: number,
    note: INoteSaveRequest
  ): Promise<ISchemaNote>;
  getSourceNote(
    projectId: string,
    sourceId: string
  ): Promise<ISourceNote | null>;
  saveSourceNote(
    projectId: string,
    sourceId: string,
    note: INoteSaveRequest
  ): Promise<ISourceNote>;
  getGroupNote(
    projectId: string,
    groupId: string
  ): Promise<IGroupNote | null>;
  saveGroupNote(
    projectId: string,
    groupId: string,
    note: INoteSaveRequest
  ): Promise<IGroupNote>;
}

class NotesApiClient implements INotesApiClient {
  /**
   * Канал чтения заметок один — маршрут заметок проекта под правом
   * «просмотр заметок» (source-explorer-notes-R06). Источник в адресе не нужен:
   * канонический идентификатор объекта задаёт его однозначно.
   */
  async getTableNotes(projectId: string, tableId: string): Promise<ITableNotes> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/notes/table/${tableId}`
    );
    if (isEmptyNoteResult(response.data)) {
      return { tableNote: null, columnNotes: [] };
    }
    return validateTableNotes(response.data, 'getTableNotes');
  }

  async saveTableNote(
    projectId: string,
    tableId: string,
    note: INoteSaveRequest
  ): Promise<ITableNote> {
    const response = await httpClient.put<unknown>(
      `/projects/${projectId}/notes/table/${tableId}`,
      note
    );
    return validateTableNote(response.data, 'saveTableNote');
  }

  async saveColumnNote(
    projectId: string,
    columnId: string,
    note: IColumnNoteSaveRequest
  ): Promise<IColumnNote> {
    const response = await httpClient.put<unknown>(
      `/projects/${projectId}/notes/column/${columnId}`,
      note
    );
    return validateColumnNote(response.data, 'saveColumnNote');
  }

  async batchAppendNote(
    projectId: string,
    columnIds: readonly string[],
    noteToAppend: string
  ): Promise<void> {
    const request: IBatchAppendNoteRequest = {
      action: "appendNote",
      columnIds,
      noteToAppend,
    };

    await httpClient.put(
      `/projects/${projectId}/notes/columns/batch`,
      request
    );
  }

  async getSchemaNote(
    projectId: string,
    sourceId: string,
    schemaId: number
  ): Promise<ISchemaNote | null> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/sources/${sourceId}/notes/schema/${schemaId}`
    );
    if (isEmptyNoteResult(response.data)) return null;
    return validateSchemaNote(response.data, 'getSchemaNote');
  }

  async saveSchemaNote(
    projectId: string,
    sourceId: string,
    schemaId: number,
    note: INoteSaveRequest
  ): Promise<ISchemaNote> {
    const response = await httpClient.put<unknown>(
      `/projects/${projectId}/sources/${sourceId}/notes/schema/${schemaId}`,
      note
    );
    return validateSchemaNote(response.data, 'saveSchemaNote');
  }

  async getSourceNote(
    projectId: string,
    sourceId: string
  ): Promise<ISourceNote | null> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/notes/source/${sourceId}`
    );
    if (isEmptyNoteResult(response.data)) return null;
    return validateSourceNote(response.data, 'getSourceNote');
  }

  async saveSourceNote(
    projectId: string,
    sourceId: string,
    note: INoteSaveRequest
  ): Promise<ISourceNote> {
    const response = await httpClient.put<unknown>(
      `/projects/${projectId}/notes/source/${sourceId}`,
      note
    );
    return validateSourceNote(response.data, 'saveSourceNote');
  }

  async getGroupNote(
    projectId: string,
    groupId: string
  ): Promise<IGroupNote | null> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/notes/group/${groupId}`
    );
    if (isEmptyNoteResult(response.data)) return null;
    return validateGroupNote(response.data, 'getGroupNote');
  }

  async saveGroupNote(
    projectId: string,
    groupId: string,
    note: INoteSaveRequest
  ): Promise<IGroupNote> {
    const response = await httpClient.put<unknown>(
      `/projects/${projectId}/notes/group/${groupId}`,
      note
    );
    return validateGroupNote(response.data, 'saveGroupNote');
  }
}

// Singleton instance
let notesApiClient: INotesApiClient | null = null;

export function getNotesApiClient(): INotesApiClient {
  notesApiClient ??= new NotesApiClient();
  return notesApiClient;
}
